// src/features/clients/pages/ReferralsPage.tsx
// ============================================================================
// PÁGINA DE REFERIDOS - EXCLUSIVA PREMIUM
// ============================================================================
//
// Muestra el historial de referidos del negocio con estadísticas,
// tabla detallada y paginación. Solo accesible para usuarios Premium.
//
// Datos desde colección: referrals/{docId}
// Campos: businessId, referrerPhone, referrerName, referrerClientId,
//          referredPhone, referredName, referredClientId,
//          pointsAwarded, bonusAwarded, branchName, createdAt
//
// Ubicación: src/features/clients/pages/ReferralsPage.tsx
// Ruta: /dashboard/referrals
// Acceso: plan PRO o superior (isPro) — Premium fusionado en PRO (2026-06-25)
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import Layout from '../../../components/layout/Layout';
import { isPro as checkIsPremium } from '../../../utils/planPermissions';
import { ProLocked } from '../../../components/pro/ProLocked';
import { clientsApi } from '../../../api/clientsApi';
import { Share2, Crown, Loader2, Users, Trophy, Gift, Star, DollarSign, ChevronLeft, ChevronRight, Info, History, Settings, MessageSquare } from 'lucide-react';
import BrandLoader from '../../../components/common/BrandLoader';
import { useError } from '../../../context/ErrorContext';
import { formatPhoneNumber } from '../../../utils/phoneUtils';
import DateTimeDisplay from '../../../components/common/DateTimeDisplay';
import SectionHowTo from '../../../components/common/SectionHowTo';
import PestanasModulo, { usePestanaActiva } from '../../../components/common/PestanasModulo';
import ReferralSettingsCard from '../components/ReferralSettingsCard';
import FilasMensajes from '../../whatsapp/components/FilasMensajes';
import { Link as RouterLink } from 'react-router-dom';

// ============================================================================
// TIPOS
// ============================================================================

interface ReferralRecord {
  id: string;
  referrerPhone: string;
  referrerName: string;
  referrerClientId: string;
  referredPhone: string;
  referredName: string;
  referredClientId: string;
  pointsAwarded: number;
  bonusAwarded: number;
  branchName: string | null;
  createdAt: Date;
}

interface ReferralStats {
  totalReferrals: number;
  totalPointsAwarded: number;
  totalBonusAwarded: number;
  topReferrer: { name: string; phone: string; count: number } | null;
}

// ============================================================================
// CONSTANTES
// ============================================================================

const ITEMS_PER_PAGE = 15;

// F3 reorden (24-oct-2026): Referidos es UNA pantalla con 3 pestañas. «Ajustes» es el bloque que antes vivía en
// Mi Negocio → Cómo premio; «Mensaje» es el aviso automático al que recomienda (editor en Mensajes automáticos).
const REF_TABS = [
  { id: 'historial', label: 'Historial', icon: History },
  { id: 'ajustes', label: 'Ajustes', icon: Settings },
  { id: 'mensaje', label: 'Mensaje', icon: MessageSquare },
];
const REF_TAB_IDS = REF_TABS.map((t) => t.id);

// ============================================================================
// HELPERS
// ============================================================================

const parseTimestamp = (ts: any): Date => {
  if (ts instanceof Date) return ts;
  return new Date(ts);
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

const ReferralsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { handleFirebaseError, clearErrors } = useError();
  const tab = usePestanaActiva(REF_TAB_IDS);

  const hasPremiumAccess = checkIsPremium(currentUser?.subscription);
  const loyaltyMode = currentUser?.loyaltyMode || 'visits';
  const unitLabel = loyaltyMode === 'cashback' ? 'pesos' : 'puntos';
  const unitPrefix = loyaltyMode === 'cashback' ? '$' : '';

  // State
  const [referrals, setReferrals] = useState<ReferralRecord[]>([]);
  const [stats, setStats] = useState<ReferralStats>({
    totalReferrals: 0,
    totalPointsAwarded: 0,
    totalBonusAwarded: 0,
    topReferrer: null,
  });
  const [loading, setLoading] = useState(true);
  const [pageChanging, setPageChanging] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [allReferrals, setAllReferrals] = useState<ReferralRecord[]>([]);
  const [totalReferrals, setTotalReferrals] = useState(0);

  // ── Carga TODO el historial de referidos desde lealify-base y calcula stats.
  //    base /loyalty/referrals devuelve hasta 200 (suficiente a esta escala), ya
  //    ordenado por fecha desc. Paginamos y agregamos en cliente.
  //    Gaps vs Firebase: base no expone teléfono del referidor/referido ni sucursal.
  const loadReferrals = useCallback(async () => {
    if (!currentUser?.businessId) {
      setReferrals([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      clearErrors();
      const r = await clientsApi.referrals();
      const records: ReferralRecord[] = (r.referrals || []).map((d: any) => ({
        id: `${d.referrer_contact_id}-${d.referred_contact_id}`,
        referrerPhone: '',
        referrerName: d.referrer_name || '',
        referrerClientId: String(d.referrer_contact_id || ''),
        referredPhone: '',
        referredName: d.referred_name || '',
        referredClientId: String(d.referred_contact_id || ''),
        pointsAwarded: Number(d.points_awarded) || 0,
        bonusAwarded: Number(d.bonus_awarded) || 0,
        branchName: null,
        createdAt: parseTimestamp(d.created_at),
      }));

      // Stats agregadas (top referidor por nº de referidos, key = id del referidor).
      let totalPoints = 0;
      let totalBonus = 0;
      const counts: Record<string, { name: string; phone: string; count: number }> = {};
      for (const rec of records) {
        totalPoints += rec.pointsAwarded;
        totalBonus += rec.bonusAwarded;
        const k = rec.referrerClientId;
        if (k) {
          if (!counts[k]) counts[k] = { name: rec.referrerName, phone: rec.referrerPhone, count: 0 };
          counts[k].count++;
        }
      }
      const entries = Object.values(counts).sort((a, b) => b.count - a.count);
      setStats({
        totalReferrals: records.length,
        totalPointsAwarded: totalPoints,
        totalBonusAwarded: totalBonus,
        topReferrer: entries[0] || null,
      });

      setAllReferrals(records);
      setTotalReferrals(records.length);
      setReferrals(records.slice(0, ITEMS_PER_PAGE));
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching referrals:', error);
      handleFirebaseError(error, 'referrals-fetch');
    } finally {
      setLoading(false);
      setPageChanging(false);
    }
  }, [currentUser?.businessId, clearErrors, handleFirebaseError]);

  // ── Initial load ──
  useEffect(() => {
    if (hasPremiumAccess && currentUser?.businessId) {
      loadReferrals();
    } else {
      setLoading(false);
    }
  }, [hasPremiumAccess, currentUser?.businessId]);

  // ── Paginación en cliente (slice del set ya cargado) ──
  const goToPage = useCallback((pageNum: number) => {
    setPageChanging(true);
    setReferrals(allReferrals.slice((pageNum - 1) * ITEMS_PER_PAGE, pageNum * ITEMS_PER_PAGE));
    setCurrentPage(pageNum);
    setPageChanging(false);
  }, [allReferrals]);

  const handleNextPage = useCallback(() => {
    if (!pageChanging && currentPage * ITEMS_PER_PAGE < totalReferrals) {
      goToPage(currentPage + 1);
    }
  }, [pageChanging, currentPage, totalReferrals, goToPage]);

  const handlePrevPage = useCallback(() => {
    if (!pageChanging && currentPage > 1) {
      goToPage(currentPage - 1);
    }
  }, [pageChanging, currentPage, goToPage]);

  const canGoNext = currentPage * ITEMS_PER_PAGE < totalReferrals;
  const canGoPrev = currentPage > 1;

  // ── Navigate to client profile ──
  const goToClient = (clientId: string) => {
    if (clientId) navigate(`/dashboard/clients/${clientId}/history`);
  };

  // ============================================================================
  // PAYWALL — NO PREMIUM
  // ============================================================================

  if (!hasPremiumAccess) {
    return (
      <Layout>
        <div className="w-full space-y-6">
          <div className="animate-fade-in-up">
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
              <Share2 className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-emerald-500" />
              Referidos
            </h1>
            <p className="mt-1 text-sm text-content-2">
              Historial y estadísticas de clientes referidos
            </p>
          </div>
          <ProLocked feature="referrals" />
        </div>
      </Layout>
    );
  }

  // ============================================================================
  // LOADING
  // ============================================================================

  if (loading && tab === 'historial') {
    return (
      <Layout>
        <div className="flex justify-center items-center py-20">
          <BrandLoader size={36} />
          <span className="ml-3 text-content-2">Cargando referidos...</span>
        </div>
      </Layout>
    );
  }

  // ============================================================================
  // RENDER PRINCIPAL
  // ============================================================================

  return (
    <Layout>
      <div className="w-full space-y-6">

        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 animate-fade-in-up">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
              <Share2 className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-emerald-500" />
              Referidos
            </h1>
            <p className="mt-1 text-sm text-content-2">
              Historial y estadísticas de clientes referidos
            </p>
          </div>
          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-500/20 text-purple-800 dark:text-purple-300">
            <Crown className="w-3 h-3 mr-1" /> PRO
          </span>
        </div>

        <PestanasModulo tabs={REF_TABS} activa={tab} testid="referidos-tabs" />

        {tab === 'ajustes' && <ReferralSettingsCard hasPremiumAccess={hasPremiumAccess} />}

        {tab === 'mensaje' && (
          <div className="space-y-3 animate-fade-in-up" data-testid="referidos-mensaje">
            <p className="text-sm text-content-2">
              El WhatsApp que recibe quien recomendó cuando su invitado se registra y se le acreditan sus {unitLabel}. Toca para encenderlo o cambiar el texto.
            </p>
            <FilasMensajes claves={['referral']} testid="referidos-mensaje-lista" />
            <p className="text-xs text-content-3">
              El resto de los avisos automáticos están en <RouterLink to="/dashboard/mensajes" className="text-accent hover:underline">Mensajes automáticos</RouterLink>.
            </p>
          </div>
        )}

        {tab === 'historial' && (<>
        {/* ── Cómo funciona (how-to de una sola vez) ── */}
        <SectionHowTo
          sectionKey="referrals"
          title="Cómo funcionan los Referidos"
          intro="Premia a tus clientes por traer a sus conocidos. Es opcional y lo controlas tú: así funciona de principio a fin."
          steps={[
            {
              title: 'Actívalo.',
              text: <>En la pestaña <strong>Ajustes</strong> (aquí arriba) enciéndelo y define cuánto gana quien recomienda y el bono de bienvenida del nuevo cliente.</>,
            },
            {
              title: '(Opcional) Da un código.',
              text: <>Asigna un código de influencer (ej. <span className="font-mono font-semibold">DOME100</span>) a tus mejores clientes desde el historial de cada uno, para que lo promocionen.</>,
            },
            {
              title: 'En caja.',
              text: <>Cuando un cliente <strong>nuevo</strong> se registra en el kiosco, el sistema le pregunta <em>"¿alguien te recomendó?"</em> y teclea el teléfono o código de quien lo invitó.</>,
            },
            {
              title: 'Automático.',
              text: <>Quien refiere recibe sus {unitLabel} (y un WhatsApp si lo activaste), el nuevo cliente su bono, y todo el historial aparece aquí.</>,
            },
          ]}
        />

        {/* ── Stats Cards ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          {/* Total referidos */}
          <div className="bg-surface rounded-lg border border-line p-4 shadow-sm">
            <div className="flex items-center text-sm font-medium text-content-3 mb-1">
              <Users className="h-4 w-4 mr-1.5 text-emerald-500" />
              Total Referidos
            </div>
            <p className="text-2xl font-bold text-content">{stats.totalReferrals}</p>
          </div>

          {/* Puntos otorgados a referidores */}
          <div className="bg-surface rounded-lg border border-line p-4 shadow-sm">
            <div className="flex items-center text-sm font-medium text-content-3 mb-1">
              {loyaltyMode === 'cashback'
                ? <DollarSign className="h-4 w-4 mr-1.5 text-green-500" />
                : <Star className="h-4 w-4 mr-1.5 text-yellow-500" />
              }
              {loyaltyMode === 'cashback' ? 'Saldo a Referidores' : 'Puntos a Referidores'}
            </div>
            <p className="text-2xl font-bold text-content">
              {unitPrefix}{loyaltyMode === 'cashback' ? stats.totalPointsAwarded.toFixed(2) : stats.totalPointsAwarded}
            </p>
          </div>

          {/* Bonus otorgados */}
          <div className="bg-surface rounded-lg border border-line p-4 shadow-sm">
            <div className="flex items-center text-sm font-medium text-content-3 mb-1">
              <Gift className="h-4 w-4 mr-1.5 text-pink-500" />
              Bonus Entregados
            </div>
            <p className="text-2xl font-bold text-content">
              {unitPrefix}{loyaltyMode === 'cashback' ? stats.totalBonusAwarded.toFixed(2) : stats.totalBonusAwarded}
            </p>
          </div>

          {/* Top referidor */}
          <div className="bg-surface rounded-lg border border-line p-4 shadow-sm">
            <div className="flex items-center text-sm font-medium text-content-3 mb-1">
              <Trophy className="h-4 w-4 mr-1.5 text-amber-500" />
              Mejor Referidor
            </div>
            {stats.topReferrer ? (
              <div>
                <p className="text-sm font-bold text-content truncate">
                  {stats.topReferrer.name || formatPhoneNumber(null, stats.topReferrer.phone)}
                </p>
                <p className="text-xs text-content-3">{stats.topReferrer.count} referido{stats.topReferrer.count !== 1 ? 's' : ''}</p>
              </div>
            ) : (
              <p className="text-sm text-content-3">Sin datos aún</p>
            )}
          </div>
        </div>

        {/* ── Referrals enabled check ── */}
        {!currentUser?.referralsEnabled && (
          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-md animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            <div className="flex">
              <Info className="h-5 w-5 text-amber-400 flex-shrink-0" />
              <div className="ml-3">
                <p className="text-sm text-amber-700">
                  El sistema de referidos está <strong>desactivado</strong>. Actívalo en la pestaña{' '}
                  <button onClick={() => navigate('/dashboard/referrals?tab=ajustes')} className="underline font-medium hover:text-amber-900">
                    Ajustes
                  </button>{' '}
                  para empezar a recibir referidos.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── Table ── */}
        <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          {referrals.length === 0 ? (
            <div className="text-center py-16 px-4">
              <Share2 className="mx-auto h-12 w-12 text-content-3 mb-4" />
              <h3 className="text-lg font-medium text-content mb-1">No hay referidos aún</h3>
              <p className="text-sm text-content-3 max-w-sm mx-auto">
                Cuando un nuevo cliente indique quién lo refirió durante su registro, aparecerá aquí.
              </p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-line">
                  <thead className="bg-surface-2">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Fecha</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Referidor</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Nuevo Cliente</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">{loyaltyMode === 'cashback' ? 'Saldo' : 'Puntos'} Referidor</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Bonus</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Sucursal</th>
                    </tr>
                  </thead>
                  <tbody className="bg-surface divide-y divide-line">
                    {referrals.map((ref) => (
                      <tr key={ref.id} className="hover:bg-surface-2 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-content-3">
                          <DateTimeDisplay date={ref.createdAt} customTimeZone={currentUser?.timeZone} format="short" />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button
                            onClick={() => goToClient(ref.referrerClientId)}
                            className="text-left hover:underline"
                          >
                            <p className="text-sm font-medium text-content">{ref.referrerName || 'Sin nombre'}</p>
                            <p className="text-xs text-content-3">{formatPhoneNumber(null, ref.referrerPhone)}</p>
                          </button>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button
                            onClick={() => goToClient(ref.referredClientId)}
                            className="text-left hover:underline"
                          >
                            <p className="text-sm font-medium text-content">{ref.referredName || 'Sin nombre'}</p>
                            <p className="text-xs text-content-3">{formatPhoneNumber(null, ref.referredPhone)}</p>
                          </button>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {ref.pointsAwarded > 0 ? (
                            <span className="inline-flex items-center text-sm font-medium text-emerald-700">
                              {loyaltyMode === 'cashback'
                                ? <DollarSign className="h-3 w-3 mr-0.5" />
                                : <Star className="h-3 w-3 mr-0.5 text-yellow-500" />
                              }
                              {unitPrefix}{loyaltyMode === 'cashback' ? ref.pointsAwarded.toFixed(2) : ref.pointsAwarded}
                            </span>
                          ) : (
                            <span className="text-sm text-content-3">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {ref.bonusAwarded > 0 ? (
                            <span className="inline-flex items-center text-sm font-medium text-pink-700 dark:text-pink-300">
                              <Gift className="h-3 w-3 mr-0.5" />
                              {unitPrefix}{loyaltyMode === 'cashback' ? ref.bonusAwarded.toFixed(2) : ref.bonusAwarded}
                            </span>
                          ) : (
                            <span className="text-sm text-content-3">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-content-3">
                          {ref.branchName || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-line">
                {referrals.map((ref) => (
                  <div key={ref.id} className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-content-3">
                        <DateTimeDisplay date={ref.createdAt} customTimeZone={currentUser?.timeZone} format="short" />
                      </span>
                      {ref.branchName && (
                        <span className="text-xs text-content-3">{ref.branchName}</span>
                      )}
                    </div>

                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <button onClick={() => goToClient(ref.referrerClientId)} className="text-left">
                          <p className="text-sm font-medium text-content truncate">
                            {ref.referrerName || formatPhoneNumber(null, ref.referrerPhone)}
                          </p>
                          <p className="text-xs text-emerald-600">Referidor</p>
                        </button>
                      </div>
                      <div className="mx-2 text-content-3">→</div>
                      <div className="flex-1 min-w-0 text-right">
                        <button onClick={() => goToClient(ref.referredClientId)} className="text-right">
                          <p className="text-sm font-medium text-content truncate">
                            {ref.referredName || formatPhoneNumber(null, ref.referredPhone)}
                          </p>
                          <p className="text-xs text-accent">Nuevo cliente</p>
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-xs">
                      {ref.pointsAwarded > 0 && (
                        <span className="inline-flex items-center font-medium text-emerald-700">
                          {loyaltyMode === 'cashback'
                            ? <DollarSign className="h-3 w-3 mr-0.5" />
                            : <Star className="h-3 w-3 mr-0.5 text-yellow-500" />
                          }
                          {unitPrefix}{loyaltyMode === 'cashback' ? ref.pointsAwarded.toFixed(2) : ref.pointsAwarded} al referidor
                        </span>
                      )}
                      {ref.bonusAwarded > 0 && (
                        <span className="inline-flex items-center font-medium text-pink-700 dark:text-pink-300">
                          <Gift className="h-3 w-3 mr-0.5" />
                          {unitPrefix}{loyaltyMode === 'cashback' ? ref.bonusAwarded.toFixed(2) : ref.bonusAwarded} bonus
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalReferrals > ITEMS_PER_PAGE && (
                <div className="bg-surface-2 px-4 py-3 flex items-center justify-between border-t border-line">
                  <div className="text-sm text-content-3">
                    Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, totalReferrals)} de {totalReferrals}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handlePrevPage}
                      disabled={!canGoPrev || pageChanging}
                      className="inline-flex items-center px-3 py-1.5 border border-line text-sm font-medium rounded-md text-content-2 bg-surface hover:bg-surface-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Anterior
                    </button>
                    <span className="text-sm text-content-2 font-medium">
                      {currentPage}
                    </span>
                    <button
                      onClick={handleNextPage}
                      disabled={!canGoNext || pageChanging}
                      className="inline-flex items-center px-3 py-1.5 border border-line text-sm font-medium rounded-md text-content-2 bg-surface hover:bg-surface-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Siguiente
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        </>)}

        {/* Page changing indicator */}
        {pageChanging && (
          <div className="fixed bottom-6 right-6 bg-surface rounded-full shadow-xl p-3 flex items-center animate-fade-in z-50 border border-line">
            <Loader2 className="h-5 w-5 text-emerald-600 animate-spin mr-2" />
            <span className="text-xs font-medium text-content-2">Cargando...</span>
          </div>
        )}

      </div>
    </Layout>
  );
};

export default ReferralsPage;